import { useState, useEffect, useRef } from 'react';
import { Switch, Button, Input, Tag, Dropdown, MenuItem, Dialog, Pagination, Table, Row, Cell } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { useWebSocketService } from '../../hooks/useWebSocketService';
import socketService from '../../services/WebsocketService';
import { gnbNodesService, GnbNode } from '../../services/gnbNodesService';
import { gnbDeployService } from '../../services/gnbDeployService';
import { automationFlowService } from '../../services/automationFlowService';
import { eiapClusterService } from '../../services/eiapClusterService';
import { EiapEndpoint, EndpointType } from '../environments/EiapClusterInterfaces';
import DeployReviewDialog from './DeployReviewDialog';
import NodeConfigWizard from './NodeConfigWizard';
import DeploymentStatusView from './DeploymentStatusView';
import { PipelineItem } from '../../const/PipelineTypes';

import styles from './GnbDeployNodes.module.css';

interface NodeSelection extends GnbNode {
  selected: boolean;
  credentialOverride: boolean;
  showPassword: boolean;
  nodeUsername: string;
  nodePassword: string;
}

interface GnbDeployNodesProps {
  pipeline: PipelineItem;
  onSubmitted?: () => void;
}

export default function GnbDeployNodes(props: GnbDeployNodesProps) {
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const { automationFlow, metadata, id: workflowId, TicketId: ticketId, gnbDeploySubmitted } = props.pipeline;
  const flowId = automationFlow?.id;
  const deploymentId = metadata?.deployment_id;
  // `submitted` drives the view: true once the ticket is submitted — either from the backend
  // (gnbDeploySubmitted on load / after refetch) OR optimistically the moment Submit succeeds
  // (justSubmitted), so the UI forwards to the Node Status view immediately without waiting for
  // the refetch round-trip.
  const [justSubmitted, setJustSubmitted] = useState(false);
  const submitted = gnbDeploySubmitted || justSubmitted;
  const deploymentStatusRef = useRef<HTMLDivElement>(null);
  const idsFetchedRef = useRef(false);
  const [nodes, setNodes] = useState<NodeSelection[]>([]);
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());
  const [allNodeIds, setAllNodeIds] = useState<Set<string>>(new Set());
  const [credentialsMap, setCredentialsMap] = useState<Map<string, { username: string; password: string; isNew: boolean; overridden: boolean }>>(new Map());
  const credentialsMapRef = useRef(credentialsMap);
  credentialsMapRef.current = credentialsMap;
  const deselectedIdsRef = useRef(deselectedIds);
  deselectedIdsRef.current = deselectedIds;
  const [reuseCredentials, setReuseCredentials] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; nodeId: string | null; nodeName: string }>({ open: false, nodeId: null, nodeName: '' });
  const [showReview, setShowReview] = useState(false);
  const [configWizardNode, setConfigWizardNode] = useState<NodeSelection | null>(null);
  const [deployPage, setDeployPage] = useState(1);
  const [deployPageSize, setDeployPageSize] = useState(20);
  const [deployTotal, setDeployTotal] = useState(0);
  const [deployFilters, setDeployFilters] = useState<{ nodeName?: string; enmRef?: string }>({});
  const [deploySortBy, setDeploySortBy] = useState<string | undefined>(undefined);
  const [deploySortOrder, setDeploySortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [showDeployFilters, setShowDeployFilters] = useState(false);

  const resolveNodes = async (automationFlowId: string): Promise<{ nodes: GnbNode[] }> => {
    try {
      showLoader();
      const { data: flow } = await automationFlowService.getById(automationFlowId);
      const clusterId = flow.eiapClusterId;
      if (!clusterId) {
        addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: 'No cluster configured for this automation flow.', color: 'red', timestamp: new Date() }]);
        return { nodes: [] };
      }

      const { data: cluster } = await eiapClusterService.getById(clusterId);
      const enmRefs = (cluster.endpoints || [])
        .filter((ep: EiapEndpoint) => ep.targetEnvToolType.name === EndpointType.ENM)
        .map((ep: { url: string }) => ep.url);

      if (!enmRefs.length) {
        addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: 'No ENMs linked to Cluster.', color: 'red', timestamp: new Date() }]);
        return { nodes: [] };
      }

      const { data: response } = await gnbNodesService.getAll({
        enmRefs,
        page: deployPage,
        limit: deployPageSize,
        ...deployFilters,
        sortBy: deploySortBy,
        sortOrder: deploySortOrder,
      });
      const gnbNodes = Array.isArray(response) ? response : response.data || [];
      setDeployTotal(Array.isArray(response) ? response.length : response.total || 0);
      // Fetch all IDs once for selection tracking
      if (!idsFetchedRef.current && enmRefs.length) {
        idsFetchedRef.current = true;
        try {
          const { data: idsResponse } = await gnbNodesService.getAllIds(enmRefs);
          setAllNodeIds(new Set(idsResponse.ids));
        } catch { idsFetchedRef.current = false; }
      }

      return { nodes: gnbNodes };
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to resolve nodes: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
      return { nodes: [] };
    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  };

  const fetchNodes = async (signal?: AbortSignal) => {
    try {
      let gnbNodes: GnbNode[] = [];
      if (flowId) {
        const result = await resolveNodes(flowId);
        if (signal?.aborted) return;
        gnbNodes = result.nodes;
      }
      const baseNodes = gnbNodes.map((n) => {
        const creds = credentialsMapRef.current.get(n.id);
        const credentialOverride = n.status === 'new' || (!!creds?.overridden);
        return { ...n, selected: !deselectedIdsRef.current.has(n.id), credentialOverride, showPassword: false, nodeUsername: creds?.username || '', nodePassword: creds?.password || '' };
      });
      // Overlay PER-TICKET staged state. The global gnb_nodes.has_staged_ciq/day1 flags do NOT
      // reflect what was staged for THIS ticket via the Node Config wizard (that lives on
      // gnb_deployment_nodes). Query the per-ticket staging so the table + review dialog show
      // "Staged" correctly and the upload fires on submit.
      if (ticketId) {
        await Promise.all(
          baseNodes.map(async (n) => {
            try {
              const ciq = await gnbNodesService.tsPreviewCiq(n.id, ticketId).then((r) => !!r.data).catch(() => false);
              const day1 = await gnbNodesService.tsListDay1(n.id, ticketId).then((r) => (r.data.files || []).length > 0).catch(() => false);
              const creds = await gnbDeployService.getStagedCreds(n.id, workflowId || '', flowId || '').then((r) => !!r.data.hasStagedCreds).catch(() => false);
              n.hasStagedCiq = ciq || n.hasStagedCiq;
              n.hasStagedDay1 = day1 || n.hasStagedDay1;
              (n as { hasStagedCreds?: boolean }).hasStagedCreds = creds;
            } catch {
              /* leave global flags as-is on error */
            }
          })
        );
      }
      setNodes(baseNodes);
      setCredentialsMap((prev) => {
        const next = new Map(prev);
        gnbNodes.forEach((n) => { if (!next.has(n.id)) next.set(n.id, { username: '', password: '', isNew: n.status === 'new', overridden: false }); });
        return next;
      });
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to fetch nodes: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchNodes(controller.signal);
    return () => controller.abort();
  }, [deployPage, deployPageSize, deployFilters, deploySortBy, deploySortOrder]);

  useEffect(() => {
    if (submitted) {
      const t = setTimeout(() => deploymentStatusRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
      return () => clearTimeout(t);
    }
  }, [submitted]);

  useWebSocketService();

  useEffect(() => {
    const unsubscribe = socketService.onEntityChange('GNB_NODE', (event) => {
      const { action, payload } = event;
      if (action === 'delete') {
        setNodes((prev) => prev.filter((n) => n.id !== payload.id));
        setDeployTotal((t) => Math.max(0, t - 1));
        setDeselectedIds((prev) => { const next = new Set(prev); next.delete(payload.id); return next; });
        setAllNodeIds((prev) => { const next = new Set(prev); next.delete(payload.id); return next; });
        setCredentialsMap((prev) => { const next = new Map(prev); next.delete(payload.id); return next; });
      } else if (action === 'create') {
        setDeployTotal((t) => t + 1);
        setAllNodeIds((prev) => { const next = new Set(prev); next.add(payload.id); return next; });
      } else {
        // update, ciq-staged, ciq-draft, day1-staged
        setNodes((prev) => prev.map((n) => n.id === payload.id ? { ...n, ...payload, selected: n.selected, credentialOverride: n.credentialOverride, showPassword: n.showPassword, nodeUsername: n.nodeUsername, nodePassword: n.nodePassword } : n));
      }
    });
    return unsubscribe;
  }, []);

  const handleSelectAll = () => {
    setDeselectedIds(new Set());
    setNodes((prev) => prev.map((n) => ({ ...n, selected: true })));
  };

  const handleDeselectAll = async () => {
    let ids = allNodeIds;
    if (ids.size === 0) {
      try {
        const { data: idsResponse } = await gnbNodesService.getAllIds();
        ids = new Set(idsResponse.ids);
        setAllNodeIds(ids);
      } catch {
        // Fallback: use current page IDs + create fake entries for the count
        ids = new Set(nodes.map((n) => n.id));
      }
    }
    setDeselectedIds(ids);
    setNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
  };

  const updateCredential = (id: string, field: 'nodeUsername' | 'nodePassword', value: string) => {
    setNodes((prev) => prev.map((n) => n.id === id ? { ...n, [field]: value } : n));
    setCredentialsMap((prev) => {
      const next = new Map(prev);
      const node = nodes.find((n) => n.id === id);
      const existing = next.get(id) || { username: '', password: '', isNew: node?.status === 'new', overridden: true };
      next.set(id, { ...existing, [field === 'nodeUsername' ? 'username' : 'password']: value });
      return next;
    });
  };

  const selectedNodes = nodes.filter((n) => n.selected);

  const deployNumPages = Math.ceil(deployTotal / deployPageSize);

  const needsCredsForNode = (node: NodeSelection) =>
    node.status === 'new' || (reuseCredentials ? node.credentialOverride : !node.credentialOverride);

  const missingCredentials = selectedNodes.some((n) => {
    if (!needsCredsForNode(n)) return false;
    return !n.nodeUsername || !n.nodePassword;
  });

  const handleDeleteNode = (nodeId: string, nodeName: string) => {
    setDeleteConfirm({ open: true, nodeId, nodeName });
  };

  const confirmDeleteNode = async () => {
    const nodeId = deleteConfirm.nodeId;
    setDeleteConfirm({ open: false, nodeId: null, nodeName: '' });
    if (!nodeId) return;
    try {
      showLoader();
      await gnbNodesService.delete(nodeId);
      await fetchNodes();
      addNotifications([{ title: 'Success', icon: 'icon-check', description: 'Node deleted.', color: 'green', timestamp: new Date() }]);
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to delete node: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  };

  return (
    <>
      <h4>Node Configuration</h4>
      <div className={styles.toolbar}>
                {nodes.length > 0 && (
                  <div className={styles.credentialsToggle}>
                    <ReactEDSWrapper
                      component={Switch}
                      checked={reuseCredentials}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setReuseCredentials(e.target.checked);
                        setNodes((prev) => prev.map((n) => ({ ...n, credentialOverride: false })));
                      }}
                      enabledLabel=""
                      disabledLabel=""
                    />
                    <div>
                      <div className={styles.credentialsTitle}>Reuse credentials</div>
                      <div className={styles.credentialsSubtitle}>
                        {reuseCredentials ? 'Existing nodes use stored credentials' : 'Provide credentials for all nodes'}
                      </div>
                    </div>
                  </div>
                )}
                <div className={styles.toolbarButtons}>
                   <ReactEDSWrapper component={Button} icon={showDeployFilters ? 'filter-off' : 'filter'} onClick={() => setShowDeployFilters(!showDeployFilters)}>
                    {showDeployFilters ? 'Close Filters' : 'Filters'}
                  </ReactEDSWrapper>
                  {showDeployFilters && (
                    <ReactEDSWrapper component={Button} icon="filter-clear" onClick={() => { setDeployFilters({}); setDeployPage(1); }}>
                      Clear Filters
                    </ReactEDSWrapper>
                  )}
                  {!submitted && (<>
                  <ReactEDSWrapper component={Button} primary disabled={(deployTotal - deselectedIds.size) === 0 || missingCredentials} onClick={() => setShowReview(true)}>
                    Submit for Upgrade
                  </ReactEDSWrapper>
                  </>)}
                </div>
              </div>

              {missingCredentials && selectedNodes.length > 0 && (
                <div className={styles.warningBar}>⚠ Some selected nodes are missing credentials</div>
              )}

              {nodes.length > 0 && (
                <div className="table-top">
                  <div className="table-top-left">
                    <div className="table-info">
                      <span>{(deployPage - 1) * deployPageSize + 1} - {Math.min(deployPage * deployPageSize, deployTotal)} of</span>
                      <span> {deployTotal} items</span>
                      <span>{` | ${deployTotal - deselectedIds.size} selected`}</span>
                    </div>
                  </div>
                </div>
              )}

              {nodes.length === 0 && !showDeployFilters && !deployFilters.nodeName && !deployFilters.enmRef ? (
                <div className={styles.emptyState}>
                  <i className={`icon icon-info ${styles.emptyStateIcon}`} />
                  <div className={styles.emptyStateTitle}>No gNB Nodes Found</div>
                  <div className={styles.emptyStateSubtitle}>Sync from RLO or upload CIQ to add nodes.</div>
                </div>
              ) : (
                <div className={styles.tableScroll}>
                <ReactEDSWrapper
                  component={Table}
                  id="deploy-nodes-table"
                  columns={[
                    { title: 'Node Name', attribute: 'nodeName', sortable: true, sortOrder: deploySortBy === 'nodeName' ? deploySortOrder : undefined, width: '150px' },
                    { title: 'ENM', attribute: 'enmRef', sortable: true, sortOrder: deploySortBy === 'enmRef' ? deploySortOrder : undefined, width: '150px' },
                    { title: 'Status', attribute: 'status', sortable: true, sortOrder: deploySortBy === 'status' ? deploySortOrder : undefined, width: '80px' },
                    { title: 'CIQ', attribute: 'ciq', width: '120px' },
                    { title: 'Day-1', attribute: 'day1', width: '80px' },
                    { title: 'Credentials', attribute: 'credentials', width: '220px' },
                    { title: 'Actions', attribute: 'actions', width: '50px' },
                  ]}
                  sortable
                  onSort={(order: 'asc' | 'desc', attr: string) => { setDeploySortBy(attr); setDeploySortOrder(order); setDeployPage(1); }}
                  multiSelect={!submitted}
                  checkboxDropdownItems={[
                    <ReactEDSWrapper key="select-all" component={MenuItem} value="select-all" disabled={deselectedIds.size === 0} onClick={handleSelectAll}>
                      Select items from all pages
                    </ReactEDSWrapper>,
                    <ReactEDSWrapper key="deselect-all" component={MenuItem} value="deselect-all" disabled={deselectedIds.size >= deployTotal} onClick={handleDeselectAll}>
                      Deselect items from all pages
                    </ReactEDSWrapper>,
                  ]}
                  onSelect={(selectedRows: any) => {
                    const newDeselected = new Set(deselectedIds);
                    const updated = nodes.map((n, i) => {
                      const isSelected = selectedRows.includes(i);
                      isSelected ? newDeselected.delete(n.id) : newDeselected.add(n.id);
                      return { ...n, selected: isSelected };
                    });
                    setDeselectedIds(newDeselected);
                    setNodes(updated);
                  }}
                  filters={showDeployFilters ? (
                    <ReactEDSWrapper component={Row} key="filter-row">
                      <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Input} placeholder="Filter" fullwidth value={deployFilters.nodeName || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setDeployFilters((prev) => ({ ...prev, nodeName: e.target.value.trim() || undefined })); setDeployPage(1); }} /></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Input} placeholder="Filter" fullwidth value={deployFilters.enmRef || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setDeployFilters((prev) => ({ ...prev, enmRef: e.target.value.trim() || undefined })); setDeployPage(1); }} /></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
                      <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
                    </ReactEDSWrapper>
                  ) : undefined}
                  showFilters={showDeployFilters}
                >
                  {nodes.length > 0 ? nodes.map((node) => {
                    const needsCreds = needsCredsForNode(node);
                    return (
                      <ReactEDSWrapper component={Row} key={node.id} selected={node.selected}>
                        <ReactEDSWrapper component={Cell}>{node.nodeName}</ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}>{node.enmRef || '—'}</ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Tag} color={node.status === 'deployed' || node.status === 'provisioned' ? 'green' : node.status === 'failed' ? 'red' : node.status === 'upgrading' ? 'orange' : 'blue'}>{node.status}</ReactEDSWrapper></ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}>
                          <div className={styles.ciqColumn}>
                            {node.currentDraftName && <ReactEDSWrapper component={Tag} color={node.status === 'deployed' ? 'green' : 'blue'}>{node.status === 'deployed' ? 'Active' : 'In Draft'}</ReactEDSWrapper>}
                            {node.hasStagedCiq && <ReactEDSWrapper component={Tag} color="orange">Staged</ReactEDSWrapper>}
                            {!node.currentDraftName && !node.hasStagedCiq && '—'}
                          </div>
                        </ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}>{node.hasStagedDay1 ? <ReactEDSWrapper component={Tag} color="orange">Staged</ReactEDSWrapper> : '—'}</ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}>
                          {needsCreds ? (
                            <div className={styles.credentialsCell}>
                              <input type="text" placeholder="Username" value={node.nodeUsername} disabled={submitted} onChange={(e) => updateCredential(node.id, 'nodeUsername', e.target.value)} className={styles.credentialsInput} autoComplete="off" />
                              <div className="deploy-nodes-password">
                                <ReactEDSWrapper component={Input} type={node.showPassword ? 'text' : 'password'} placeholder="Password" value={node.nodePassword} disabled={submitted} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCredential(node.id, 'nodePassword', e.target.value)} autoComplete="new-password" icon={`icon ${node.showPassword ? 'icon-invisible' : 'icon-eye'}`} onIconClick={() => setNodes((prev) => prev.map((n) => n.id === node.id ? { ...n, showPassword: !n.showPassword } : n))} />
                              </div>
                            </div>
                          ) : (
                            <span className={styles.reuseStored}>Reuse stored</span>
                          )}
                        </ReactEDSWrapper>
                        <ReactEDSWrapper component={Cell}>
                          <ReactEDSWrapper component={Dropdown} more className="wf-con-menu">
                            {[
                              ...(!submitted ? [<ReactEDSWrapper key="nodeconfig" component={MenuItem} onClick={() => setConfigWizardNode(node)}>Node Configuration</ReactEDSWrapper>] : []),
                              ...(!submitted ? [
                              <hr key="sep3" />,
                              <ReactEDSWrapper key="deletenode" component={MenuItem} icon="trashcan" onClick={() => handleDeleteNode(node.id, node.nodeName)}>Delete Node</ReactEDSWrapper>,
                              ] : []),
                            ]}
                          </ReactEDSWrapper>
                        </ReactEDSWrapper>
                      </ReactEDSWrapper>
                    )
                  }) : (
                    <ReactEDSWrapper component={Row} disabled className={styles.emptyRow}>
                      <ReactEDSWrapper component={Cell} colSpan={7}>
                        <div className={styles.emptyState}>
                          <i className={`icon icon-info ${styles.emptyStateIcon}`} />
                          <div className={styles.emptyStateTitle}>No results found</div>
                          <div className={styles.emptyStateSubtitle}>Try adjusting your filters.</div>
                        </div>
                      </ReactEDSWrapper>
                    </ReactEDSWrapper>
                  )}
                </ReactEDSWrapper>
                </div>
              )}
              {nodes.length > 0 && (
                <ReactEDSWrapper
                  component={Pagination}
                  numPages={deployNumPages || 1}
                  selectedPage={deployPage}
                  onPaginationChangePage={setDeployPage}
                  optionsList={[
                    { value: '20', label: '20', active: deployPageSize === 20 },
                    { value: '50', label: '50', active: deployPageSize === 50 },
                    { value: '100', label: '100', active: deployPageSize === 100 },
                  ]}
                  onPaginationChangeSelect={(size: string) => { setDeployPageSize(Number(size)); setDeployPage(1); }}
                  withActions
                />
              )}

      {workflowId && submitted && (
        <div ref={deploymentStatusRef} className={styles.deploymentWrapper}>
          <section className={styles.deploymentSection}>
            <header className={styles.deploymentHeader}>
              <h3 className={styles.deploymentTileTitle}>Node Status</h3>
            </header>
            <DeploymentStatusView workflowId={workflowId} deploymentId={deploymentId || undefined} />
          </section>
        </div>
      )}

      {deleteConfirm.open && (
        <ReactEDSWrapper
          component={Dialog}
          show
          title="Delete Node"
          onClose={() => setDeleteConfirm({ open: false, nodeId: null, nodeName: '' })}
          bottom={
            <>
              <ReactEDSWrapper component={Button} warning onClick={confirmDeleteNode}>
                Delete Node
              </ReactEDSWrapper>
            </>
          }
        >
          <p>
            Are you sure you want to delete <strong>{deleteConfirm.nodeName}</strong>? This action cannot be undone.
          </p>
        </ReactEDSWrapper>
      )}
      {configWizardNode && (
        <NodeConfigWizard
          node={configWizardNode}
          ctx={{ workflowTicketId: workflowId || '', ticketId: ticketId || '', automationFlowId: flowId || '' }}
          onClose={() => setConfigWizardNode(null)}
          onSaved={() => { fetchNodes(); }}
        />
      )}
      {showReview && (
        <DeployReviewDialog
          nodes={nodes.map((n) => ({
            id: n.id,
            nodeName: n.nodeName,
            currentVersion: (n as { currentVersion?: number | null }).currentVersion ?? null,
            hasStagedCiq: n.hasStagedCiq,
            hasStagedDay1: n.hasStagedDay1,
            credsProvided: !!credentialsMap.get(n.id)?.username || n.status === 'new' || n.credentialOverride || !!(n as { hasStagedCreds?: boolean }).hasStagedCreds
          }))}
          deselectedIds={deselectedIds}
          credentialsMap={credentialsMap}
          ticketId={ticketId || ''}
          automationFlowId={flowId || ''}
          deploymentId={deploymentId || ''}
          workflowId={workflowId || ''}
          onClose={() => setShowReview(false)}
          onSuccess={() => {
            setShowReview(false);
            setJustSubmitted(true);
            fetchNodes();
            props.onSubmitted?.();
          }}
        />
      )}
    </>
  );
}
